import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  agentService,
  AgentCampaign,
  AgentCallActivity,
  AgentStats,
} from "../../services/agentService";
import Button from "../../components/UI/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/UI/Card";
import LoadingSpinner from "../../components/UI/LoadingSpinner";
import TeamStatusPanel from "../../components/Agent/TeamStatusPanel";
import { useWebRTC, AgentCallStatus } from "../../contexts/WebRTCContext";
import toast from "react-hot-toast";

// ─── AgentDashboardPage ──────────────────────────────────────────────────────

const AgentDashboardPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const fromManualDial = (location.state as any)?.fromManualDial === true;
  const manualDialCampaignId = (location.state as any)?.campaignId as string | undefined;

  // ─── Global WebRTC (from Layout's WebRTCProvider) ──────────────────
  const {
    status,
    error,
    isMuted,
    reconnect,
    hangUp,
    toggleMute,
    handleDisposition,
    callLeadInfo,
    seconds,
    dispositionSubmitted,
    setOnDispositionComplete,
  } = useWebRTC();

  // ─── Dashboard Data ────────────────────────────────────────────────────
  const [stats, setStats] = useState<AgentStats | null>(null);
  const [campaigns, setCampaigns] = useState<AgentCampaign[]>([]);
  const [recentCalls, setRecentCalls] = useState<AgentCallActivity[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [dataError, setDataError] = useState<string | null>(null);
  const [statusMenuCallId, setStatusMenuCallId] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  // Close status dropdown on outside click
  useEffect(() => {
    if (!statusMenuCallId) return;
    const handler = () => setStatusMenuCallId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [statusMenuCallId]);

  // ─── Fetch Dashboard Data ──────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingData(true);
        setDataError(null);
        const [statsData, campaignData, callData] = await Promise.all([
          agentService.getMyStats(),
          agentService.getMyCampaigns(),
          agentService.getMyCalls(10),
        ]);
        setStats(statsData || null);
        setCampaigns(campaignData || []);
        setRecentCalls(callData || []);
      } catch (err: any) {
        console.error("Failed to load agent data:", err);
        setDataError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load dashboard data",
        );
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, []);

  // ─── Team Call Notifications ──────────────────────────────────────────
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.type === 'took-call' && detail?.agentName) {
        toast(`${detail.agentName} is now on a call`, { icon: '📞', duration: 3000 });
      }
    };
    window.addEventListener('team-call-event', handler);
    return () => window.removeEventListener('team-call-event', handler);
  }, []);

  // ─── Register disposition-complete callback for manual-dial return ────
  useEffect(() => {
    if (fromManualDial) {
      setOnDispositionComplete((payload) => {
        navigate('/agent-manual-dial', {
          state: {
            returnedFromCall: true,
            campaignId: manualDialCampaignId,
            autoAdvance: true,
            leadId: payload?.leadId || null,
            disposition: payload?.disposition || null,
          },
        });
      });
      return () => setOnDispositionComplete(null);
    }
  }, [fromManualDial, manualDialCampaignId, navigate, setOnDispositionComplete]);

  // ─── Dashboard Call Actions ────────────────────────────────────────────
  const handleCallStatusChange = useCallback(
    async (call: AgentCallActivity, newStatus: string) => {
      if (!call.lead_id || updatingStatus) return;
      setUpdatingStatus(call.leg_id);
      try {
        await agentService.setDisposition({
          leadId: call.lead_id,
          callAttemptId: call.call_attempt_id || null,
          disposition: newStatus,
        });
        // Update local state
        setRecentCalls((prev) =>
          prev.map((c) =>
            c.leg_id === call.leg_id ? { ...c, lead_status: newStatus } : c
          )
        );
        setStatusMenuCallId(null);
        toast.success(`Lead marked as ${newStatus.replace(/_/g, ' ')}`);
      } catch (err: any) {
        toast.error(err?.response?.data?.message || 'Failed to update status');
      } finally {
        setUpdatingStatus(null);
      }
    },
    [updatingStatus],
  );

  // ─── Helpers ──────────────────────────────────────────────────────────
  const formatDuration = (secs: number) =>
    `${Math.floor(secs / 60)
      .toString()
      .padStart(2, "0")}:${(secs % 60).toString().padStart(2, "0")}`;

  const formatLeadStatusLabel = (status?: string | null) => {
    if (!status) return "Set Status";
    return status
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const statusColors: Record<AgentCallStatus, string> = {
    loading: "text-muted-foreground",
    connecting: "text-yellow-500",
    ready: "text-green-500",
    ringing: "text-blue-500 animate-pulse",
    "in-call": "text-green-600",
    error: "text-destructive",
  };

  const statusLabels: Record<AgentCallStatus, string> = {
    loading: "Initializing...",
    connecting: "Connecting to Telnyx...",
    ready: "Ready — Waiting for calls",
    ringing: "Incoming Call!",
    "in-call": "On Call",
    error: "Connection Error",
  };

  // ─── Render ────────────────────────────────────────────────────────────
  const formatDurationFull = (secs: number) => {
    if (secs < 60) return `${secs}s`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
    return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
  };

  return (
    <div className="space-y-6">
      {/* ─── Page Header ─────────────────────────────────────────── */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Agent Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Monitor your campaigns, calls and performance
        </p>
      </div>

      {/* ─── Data Error Banner ───────────────────────────────────── */}
      {dataError && (
        <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-4">
          <div className="flex items-center gap-2">
            <svg
              className="h-5 w-5 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm text-red-700 dark:text-red-300">
              {dataError}
            </span>
          </div>
        </div>
      )}

      {/* ─── Stats Overview Cards ───────────────────────────────── */}
      {loadingData ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="animate-pulse space-y-2">
                  <div className="h-3 bg-muted rounded w-20" />
                  <div className="h-7 bg-muted rounded w-12" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : stats ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Today's Calls
              </div>
              <div className="text-2xl font-bold text-foreground mt-1">
                {stats.todayCalls}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Total Calls
              </div>
              <div className="text-2xl font-bold text-foreground mt-1">
                {stats.totalCalls}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Completed
              </div>
              <div className="text-2xl font-bold text-green-600 mt-1">
                {stats.completedCalls}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Failed
              </div>
              <div className="text-2xl font-bold text-red-500 mt-1">
                {stats.failedCalls}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Avg Duration
              </div>
              <div className="text-2xl font-bold text-foreground mt-1">
                {formatDurationFull(stats.avgDuration)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Active Campaigns
              </div>
              <div className="text-2xl font-bold text-blue-600 mt-1">
                {stats.activeCampaigns}
              </div>
              <div className="text-xs text-muted-foreground">
                {stats.totalLeads} total leads
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
      {/* ─── Agent Phone Status ──────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Agent Phone</CardTitle>
            <div className="flex items-center gap-2">
              <span
                className={`inline-block h-3 w-3 rounded-full ${
                  status === "ready"
                    ? "bg-green-500"
                    : status === "in-call"
                      ? "bg-green-600 animate-pulse"
                      : status === "ringing"
                        ? "bg-blue-500 animate-pulse"
                        : status === "connecting"
                          ? "bg-yellow-500"
                          : status === "error"
                            ? "bg-red-500"
                            : "bg-gray-400"
                }`}
              />
              <span className={`text-sm font-medium ${statusColors[status]}`}>
                {statusLabels[status]}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {status === "loading" && (
            <div className="flex items-center gap-3 text-muted-foreground">
              <LoadingSpinner size="sm" />
              Initializing Telnyx WebRTC...
            </div>
          )}
          {status === "connecting" && (
            <div className="flex items-center gap-3 text-muted-foreground">
              <LoadingSpinner size="sm" />
              Connecting to Telnyx...
            </div>
          )}
          {status === "error" && (
            <div className="space-y-3">
              <div className="text-destructive text-sm">
                {error || "Unable to connect to Telnyx WebRTC."}
              </div>
              <Button variant="outlined" size="sm" onClick={reconnect}>
                Retry Connection
              </Button>
            </div>
          )}
          {status === "ready" && (
            <div className="text-sm text-muted-foreground">
              Your browser is connected and ready to receive calls.
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Team Status ────────────────────────────────────────── */}
      <TeamStatusPanel />

      {/* ─── Active Call ────────────────────────────────────────── */}
      {status === "in-call" && (
        <Card className="border-green-500 border-2">
          <CardHeader>
            <CardTitle>On Call</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              {/* Lead Name */}
              {callLeadInfo?.leadName && (
                <div className="text-lg font-semibold text-foreground">
                  {callLeadInfo.leadName}
                </div>
              )}
              {/* Phone */}
              <div className={`font-medium text-muted-foreground ${callLeadInfo?.leadName ? 'text-sm' : 'text-lg font-semibold text-foreground'}`}>
                {callLeadInfo?.leadPhone || 'Unknown'}
              </div>
              {/* Email */}
              {callLeadInfo?.leadEmail && (
                <div className="text-sm text-muted-foreground">
                  {callLeadInfo.leadEmail}
                </div>
              )}
              {/* Company */}
              {callLeadInfo?.companyName && (
                <div className="text-sm text-muted-foreground">
                  🏢 {callLeadInfo.companyName}
                </div>
              )}
              {/* Campaign */}
              {callLeadInfo?.campaignName && (
                <div className="text-xs text-muted-foreground">
                  Campaign: {callLeadInfo.campaignName}
                </div>
              )}
            </div>
            <div className="text-2xl font-mono font-bold">
              {formatDuration(seconds)}
            </div>
            <div className="flex gap-3">
              <Button variant="outlined" onClick={toggleMute}>
                {isMuted ? "Unmute" : "Mute"}
              </Button>
              <Button variant="destructive" onClick={hangUp}>
                Hang Up
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── Assigned Campaigns ─────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Assigned Campaigns</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingData ? (
            <div className="flex items-center gap-3 text-muted-foreground">
              <LoadingSpinner size="sm" />
              Loading campaigns...
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No campaigns assigned to you yet.
            </div>
          ) : (
            <div className="space-y-3">
              {campaigns.map((campaign) => (
                <div
                  key={campaign.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors"
                >
                  <div className="space-y-1">
                    <div className="font-medium">{campaign.name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          campaign.status === "active"
                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                            : campaign.status === "paused"
                              ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                              : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
                        }`}
                      >
                        {campaign.status}
                      </span>
                      <span>{campaign.totalLeads} leads</span>
                      <span>
                        {campaign.stats.completed}/{campaign.stats.total} calls
                      </span>
                    </div>

                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Recent Call Activity ───────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Call Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingData ? (
            <div className="flex items-center gap-3 text-muted-foreground">
              <LoadingSpinner size="sm" />
              Loading calls...
            </div>
          ) : recentCalls.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No call activity yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">
                      Lead
                    </th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">
                      Campaign
                    </th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">
                      Call Status
                    </th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">
                      Lead Status
                    </th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">
                      Duration
                    </th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">
                      Time
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {recentCalls.map((call) => (
                    <tr
                      key={call.leg_id}
                      className="border-b border-border/50 hover:bg-accent/30"
                    >
                      <td className="py-2 px-3">
                        <div className="font-medium">
                          {call.lead_name || "Unknown"}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {call.lead_phone}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-muted-foreground">
                        {call.campaign_name || "—"}
                      </td>
                      <td className="py-2 px-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            call.leg_status === "in-progress" ||
                            call.leg_status === "completed"
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                              : call.leg_status === "no-answer" ||
                                  call.leg_status === "busy"
                                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                                : call.leg_status === "failed" ||
                                    call.leg_status === "canceled"
                                  ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                  : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
                          }`}
                        >
                          {call.leg_status}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <div className="relative">
                          {call.lead_id ? (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); setStatusMenuCallId(statusMenuCallId === call.leg_id ? null : call.leg_id); }}
                                disabled={updatingStatus === call.leg_id}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                                  call.lead_status === 'interested'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                                    : call.lead_status === 'not_interested'
                                    ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50'
                                    : call.lead_status === 'follow_up'
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50'
                                    : call.lead_status === 'out_of_business'
                                    ? 'bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800/70'
                                    : call.lead_status === 'wrong_number'
                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 hover:bg-yellow-200 dark:hover:bg-yellow-900/50'
                                    : call.lead_status === 'unqualified'
                                    ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/50'
                                    : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                                }`}
                              >
                                {updatingStatus === call.leg_id ? '...' : formatLeadStatusLabel(call.lead_status)}
                                <svg className="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                              {statusMenuCallId === call.leg_id && (
                                <div className="absolute left-0 top-full mt-1 w-44 bg-card border border-border rounded-lg shadow-lg z-50 py-1">
                                  {[
                                    { value: 'interested', label: '👍 Interested', color: 'text-green-700 dark:text-green-400' },
                                    { value: 'not_interested', label: '👎 Not Interested', color: 'text-red-700 dark:text-red-400' },
                                    { value: 'follow_up', label: '📅 Follow Up', color: 'text-blue-700 dark:text-blue-400' },
                                    { value: 'app_in', label: '📲 App In', color: 'text-sky-700 dark:text-sky-400' },
                                    { value: 'app_out', label: '📱 App Out', color: 'text-indigo-700 dark:text-indigo-400' },
                                    { value: 'out_of_business', label: '🏚️ Out of Business', color: 'text-gray-700 dark:text-gray-400' },
                                    { value: 'wrong_number', label: '📵 Wrong Number', color: 'text-yellow-700 dark:text-yellow-400' },
                                    { value: 'unqualified', label: '⛔ Unqualified', color: 'text-orange-700 dark:text-orange-400' },
                                  ].map((opt) => (
                                    <button
                                      key={opt.value}
                                      onClick={() => handleCallStatusChange(call, opt.value)}
                                      className={`w-full text-left px-3 py-1.5 text-xs hover:bg-accent/50 transition-colors ${opt.color} ${
                                        call.lead_status === opt.value ? 'font-semibold bg-accent/30' : ''
                                      }`}
                                    >
                                      {opt.label}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-muted-foreground">
                        {call.duration_seconds
                          ? formatDuration(call.duration_seconds)
                          : "—"}
                      </td>
                      <td className="py-2 px-3 text-muted-foreground text-xs">
                        {new Date(call.routed_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AgentDashboardPage;
